import React, { useEffect, useRef } from "react";

const HERO_VIDEO =
  "https://videos.pexels.com/video-files/17197593/17197593-hd_1920_1080_30fps.mp4";
const HERO_VIDEO_FALLBACK_POSTER =
  "https://images.pexels.com/videos/17197593/pexels-photo-17197593.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920";
const SUBMARINER_IMG =
  "https://images.unsplash.com/photo-1662384197911-e82189f4dc60?fm=jpg&q=85&w=1400&auto=format&fit=crop";

export default function Hero() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, []);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen h-auto md:h-screen md:min-h-[760px] w-full overflow-hidden bg-[#001226]"
    >
      {/* Background video */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_VIDEO_FALLBACK_POSTER}
          data-testid="hero-bg-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        {/* Navy gradient overlays for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#001226] via-[#001226]/70 to-[#001226]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001226]/85 via-[#001226]/40 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-end md:items-center pt-28 md:pt-20 pb-12 md:pb-16">
        <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-7 animate-fade-up">
              <p
                data-testid="hero-eyebrow"
                className="text-[10px] md:text-[11px] uppercase tracking-[0.3em] md:tracking-[0.4em] text-accent mb-6 md:mb-8"
              >
                Maison Horlogère — Lausanne, Suisse
              </p>
              <h1 className="font-serif font-light text-white text-4xl xs:text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tight">
                Le luxe
                <br />
                devient{" "}
                <span className="italic text-zinc-100">accessible.</span>
              </h1>
              <p className="mt-6 md:mt-8 max-w-xl text-zinc-100 text-sm md:text-lg leading-relaxed font-light">
                Paiement échelonné jusqu'à 36 mois sur une sélection de
                garde-temps d'exception. La haute horlogerie suisse, sans
                compromis et à votre rythme.
              </p>

              <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
                <a
                  href="#paiement"
                  data-testid="hero-cta-paiement"
                  className="inline-flex items-center justify-center gap-3 px-6 md:px-8 py-4 bg-accent text-white text-[10px] md:text-[11px] uppercase tracking-[0.22em] md:tracking-[0.25em] hover:bg-accent-dark transition-colors text-center"
                >
                  Paiement échelonné
                  <span aria-hidden>→</span>
                </a>
                <a
                  href="#estimation"
                  data-testid="hero-cta-estimation"
                  className="inline-flex items-center justify-center gap-3 px-6 md:px-8 py-4 border border-white/40 text-white text-[10px] md:text-[11px] uppercase tracking-[0.22em] md:tracking-[0.25em] hover:border-accent hover:text-accent transition-colors text-center"
                >
                  Estimation gratuite
                </a>
              </div>

              {/* Trust strip */}
              <div className="mt-8 md:mt-12 flex flex-col sm:flex-row sm:flex-wrap gap-y-3 gap-x-8 md:gap-x-10 text-[10px] uppercase tracking-[0.22em] md:tracking-[0.25em] text-zinc-200">
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-accent rounded-full" />
                  Sans frais cachés
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-accent rounded-full" />
                  Réponse en 24h
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-accent rounded-full" />
                  Garantie 24 mois
                </span>
              </div>
            </div>

            {/* Right: featured Submariner card */}
            <aside
              data-testid="hero-featured-card"
              className="lg:col-span-5 animate-fade-in lg:ml-auto w-full max-w-sm md:max-w-md mx-auto lg:mx-0"
            >
              <div className="relative bg-white/95 backdrop-blur-md border border-white/30 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
                <div className="aspect-[4/5] overflow-hidden bg-[#F8F4EC]">
                  <img
                    src={SUBMARINER_IMG}
                    alt="Rolex Submariner Date"
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
                {/* Orange price tag */}
                <div className="absolute top-6 left-6 bg-accent text-white px-4 py-3">
                  <p className="text-[9px] uppercase tracking-[0.3em] opacity-90">
                    Paiement
                  </p>
                  <p className="font-serif text-2xl leading-none mt-1">
                    150.-<span className="text-sm">/mois</span>
                  </p>
                </div>
                <div className="p-6 md:p-8">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-accent mb-2">
                    Pièce du moment
                  </p>
                  <h3 className="font-serif text-3xl text-[#001226] font-light leading-tight">
                    Rolex <span className="italic">Submariner</span>
                  </h3>
                  <p className="mt-3 text-sm text-[#1E2A40] font-light leading-relaxed">
                    Acier Oystersteel · Lunette céramique noire · 41 mm.
                    Disponible en paiement échelonné jusqu'à 36 mois sans
                    frais cachés.
                  </p>
                  <a
                    href="#simulateur"
                    data-testid="hero-featured-cta"
                    className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#001226] border-b border-[#001226] pb-1 hover:text-accent hover:border-accent transition-colors"
                  >
                    Simuler ma mensualité
                    <span aria-hidden>→</span>
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Scroll cue — desktop only */}
      <div className="hidden md:flex absolute bottom-8 right-8 md:right-12 flex-col items-end gap-2 text-zinc-300 text-[10px] uppercase tracking-[0.3em] z-10">
        <span>Scroll</span>
        <span className="block w-px h-12 bg-zinc-400" />
      </div>
    </section>
  );
}
